'use client';

import { c as _c } from "react/compiler-runtime";
import './article-tabs.scss';
import { t } from 'i18next';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { TabGroup, TabGroupProps } from '@sola/ark-core/molecules/tab-group/tab-group.tsx';
import { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.ts';
import { useListArticlesQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import { QueryResponsePublished } from '@sola/skippy-search-sdk/lib/schema.ts';
import { useTabGroupQueryParams } from '../../utils/query-params.tsx';
import { ArticleControls } from './article-controls.tsx';
import { ArticleListProps } from './article-list.tsx';
import { ArticleTabsEnum, ArticleTermList } from './article-term-list.tsx';
const getTabItems = (seoLevel: number, articleType: ArticleTypesEnum, limit?: number, articleData?: QueryResponsePublished): TabGroupProps => {
  const seoLevelTabFirst = seoLevel;
  const seoLevelTab = seoLevel === 1 ? 2 : seoLevel;
  const tabs = {
    title: 'Article Title',
    items: [{
      // todo: move to article.tabs
      title: t(`articles:tabs.${ArticleTabsEnum.enum.all}`),
      tabKey: ArticleTabsEnum.enum.all as ArticleTabsEnum,
      body: <ArticleControls seoLevel={seoLevelTabFirst + 1} articleType={articleType} limit={limit || 12} />,
      seoLevel: seoLevelTabFirst
    }]
  };
  const facetsTopics = articleData?.facets.filter(facet => facet?.field === 'taxonomy.articleTopics.keyword') || [];
  const tabActiveTopics = facetsTopics.some(facet => facet?.values && facet.values.length > 3);
  const facetsThemes = articleData?.facets.filter(facet => facet?.field === 'taxonomy.articleThemes.keyword') || [];
  const tabActiveThemes = facetsThemes.some(facet => facet?.values && facet.values.length > 3);
  if (tabActiveTopics || tabActiveThemes) {
    tabs.items.push({
      title: t(`articles:tabs.${ArticleTabsEnum.enum.topic}`),
      tabKey: ArticleTabsEnum.enum.topic,
      body: <div className="py-roomy-sm">
          <ArticleTermList seoLevel={seoLevelTab + 1} tabGroup={ArticleTabsEnum.enum.topic} articleType={articleType} facets={{
          topic: facetsTopics,
          theme: facetsThemes
        }} />
        </div>,
      seoLevel: seoLevelTab
    });
  }
  const facetsCountry = articleData?.facets.filter(facet => facet?.field === 'taxonomy.programCountry.keyword') || [];
  const tabActiveCountry = facetsCountry.some(facet => facet?.values && facet.values.length > 3);
  if (tabActiveCountry) {
    tabs.items.push({
      title: t(`articles:tabs.${ArticleTabsEnum.enum.country}`),
      tabKey: ArticleTabsEnum.enum.country,
      body: <div className="py-roomy-sm">
          <ArticleTermList seoLevel={seoLevelTab + 1} tabGroup={ArticleTabsEnum.enum.country} articleType={articleType} facets={{
          country: facetsCountry
        }} />
        </div>,
      seoLevel: seoLevelTab
    });
  }

  // @TODO: bring back when content hub search is supported
  // tabs.items.push({
  //   title: t(`articles:tabs.${ArticleTabsEnum.Enum.search}`),
  //   tabKey: ArticleTabsEnum.Enum.search,
  //   body: (
  //     <div className="py-roomy-sm">
  //       <SearchBar
  //         title={t('articles:searchContentHub')}
  //         placeholder={t('articles:searchPlaceholder')}
  //         seoLevel={seoLevelTab + 1}
  //       />
  //     </div>
  //   ),
  //   icon: 'search-sm',
  //   seoLevel: seoLevelTab,
  // });

  return tabs;
};
export const ArticleTabs: React.FC<ArticleListProps> = t0 => {
  const $ = _c(13);
  const {
    seoLevel: t1,
    articleType: t2,
    limit
  } = t0;
  const seoLevel = t1 === undefined ? 2 : t1;
  const articleType = t2 === undefined ? ArticleTypesEnum.enum.blogPost : t2;
  const {
    tabGroupFilters,
    setTabGroupFilters
  } = useTabGroupQueryParams();
  const t3 = articleType || undefined;
  let t4;
  if ($[0] !== t3) {
    t4 = {
      perPage: 1,
      articleType: t3
    };
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const articleFacetQuery = useListArticlesQuery(t4);
  const {
    data: articleData
  } = articleFacetQuery;
  let t5;
  if ($[2] !== articleData || $[3] !== articleType || $[4] !== limit || $[5] !== seoLevel) {
    t5 = getTabItems(seoLevel, articleType, limit, articleData);
    $[2] = articleData;
    $[3] = articleType;
    $[4] = limit;
    $[5] = seoLevel;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const tabItems = t5;
  if (tabItems.items?.length === 1) {
    return tabItems.items[0].body;
  }
  let t6;
  if ($[7] !== setTabGroupFilters) {
    t6 = v => {
      setTabGroupFilters(v as ArticleTabsEnum);
    };
    $[7] = setTabGroupFilters;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6 || $[10] !== tabGroupFilters || $[11] !== tabItems) {
    t7 = <TabGroup {...tabItems} activeValue={tabGroupFilters} onChange={t6} />;
    $[9] = t6;
    $[10] = tabGroupFilters;
    $[11] = tabItems;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};

/**
 * Skeleton component for ArticleTabs.
 * Matches the dimensions and layout of the ArticleTabs component.
 */
export const ArticleTabsSkeleton = () => {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["all", "topic", "country"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const tabSkeletonItems = t0 as const;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Array.from({
      length: 8
    }, _temp);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const articleCardSkeletonKeys = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex gap-snug-sm overflow-x-auto pb-snug-sm">{tabSkeletonItems.map(_temp2)}</div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-col gap-snug-xs"><Skeleton height={18} width={140} /><Skeleton height={14} width={100} className="hidden md:block" /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="ArticleFilter"><div className="ArticleFilter__container flex flex-col md:flex-row md:justify-between md:align-center gap-comfy-lg">{t3}<div className="flex gap-comfy-sm w-full md:w-auto"><Skeleton height={40} width="100%" borderRadius="0.5rem" className="md:w-60" /><Skeleton height={40} width={80} borderRadius="0.5rem" /></div></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="grid gap-comfy-sm @md:gap-comfy-lg grid-cols-1 @sm:grid-cols-2 @lg:grid-cols-3 @xl:grid-cols-4">{articleCardSkeletonKeys.map(_temp3)}</div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="TabGroup grid gap-comfy-lg">{t2}<div className="grid gap-comfy-xl">{t4}{t5}<div className="flex justify-center mt-comfy-lg pt-comfy-lg border-t border-neutral-mild"><div className="flex gap-snug-sm"><Skeleton height={48} width={48} borderRadius="0.5rem" /><Skeleton height={48} width={96} borderRadius="0.5rem" /><Skeleton height={48} width={48} borderRadius="0.5rem" /></div></div></div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
};
function _temp(_, idx) {
  return `article-card-skeleton-${idx + 1}`;
}
function _temp2(tab) {
  return <Skeleton key={`article-tab-${tab}`} height={40} width={110} borderRadius="0.5rem" style={{
    minWidth: "6.5rem"
  }} />;
}
function _temp3(key) {
  return <div key={key} className="flex flex-col gap-comfy-sm"><Skeleton width="100%" height="auto" style={{
      aspectRatio: "16/9"
    }} borderRadius="0.5rem" /><Skeleton height={16} width="40%" /><div className="flex flex-col gap-snug-xs"><Skeleton height={22} width="100%" /><Skeleton height={22} width="85%" /></div><div className="flex flex-col gap-snug-xs"><Skeleton height={16} width="100%" /><Skeleton height={16} width="95%" /></div></div>;
}